import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, ShieldCheck, AlertCircle, Mail, Check, FileCheck2, Download } from "lucide-react";
import { api } from "../lib/api";
import { PostmanIcon } from "../components/PostmanIcon";

const PRESCRIPTION_LABEL = {
  short_term_breakthrough: { en: "Short-Term Breakthrough", zh: "短期突破" },
  heart_healing: { en: "Heart Healing", zh: "心痛療癒" },
  soul_covenant: { en: "Soul Covenant", zh: "靈魂約定" },
};

const labelFor = (tag, lang) => {
  const entry = PRESCRIPTION_LABEL[tag];
  if (!entry) return tag;
  return lang && lang.startsWith("zh") ? entry.zh : entry.en;
};

export default function RescuePage() {
  const { t, i18n } = useTranslation();
  const isZH = (i18n.language || "en").startsWith("zh");

  const [identifier, setIdentifier] = useState("");
  const [lookupState, setLookupState] = useState("idle"); // idle|loading|ok|error
  const [lookup, setLookup] = useState(null);
  const [error, setError] = useState("");

  // email update sub-state
  const [showEditForm, setShowEditForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [updateState, setUpdateState] = useState("idle"); // idle|saving|ok|error
  const [updateError, setUpdateError] = useState("");

  const handleLookup = async (e) => {
    e?.preventDefault();
    if (!identifier.trim()) return;
    setLookupState("loading");
    setError("");
    setLookup(null);
    setShowEditForm(false);
    setUpdateState("idle");
    setUpdateError("");
    try {
      const r = await api.post("/rescue/lookup", { identifier: identifier.trim() });
      setLookup(r.data);
      setLookupState("ok");
    } catch (e) {
      setError(e.response?.data?.detail || String(e));
      setLookupState("error");
    }
  };

  const handleUpdate = async (e) => {
    e?.preventDefault();
    if (!newEmail) return;
    setUpdateState("saving");
    setUpdateError("");
    try {
      const r = await api.post("/rescue/update-email", {
        identifier: identifier.trim(),
        new_email: newEmail.trim(),
      });
      setLookup({ ...lookup, capsule: r.data.capsule });
      setUpdateState("ok");
      setShowEditForm(false);
    } catch (e) {
      setUpdateError(e.response?.data?.detail || String(e));
      setUpdateState("error");
    }
  };

  const capsule = lookup?.mode === "stripe" ? lookup.capsule : null;
  const emailModeMessage = lookup?.mode === "email" ? lookup : null;

  return (
    <div className="min-h-screen px-6 py-12 md:py-16" data-testid="rescue-page">
      <div className="max-w-2xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] mb-10"
        >
          <ArrowLeft size={12} /> {t("rescue.back_home")}
        </Link>

        <div className="text-center mb-10">
          <PostmanIcon size={56} className="mx-auto mb-5" />
          <p className="text-[11px] uppercase tracking-[0.42em] text-[var(--wax)] mb-3">
            {t("rescue.kicker")}
          </p>
          <h1
            className={`${
              isZH
                ? "font-cn text-[26px] sm:text-3xl md:text-[36px] lg:text-[40px] leading-[1.3] md:whitespace-nowrap"
                : "font-display text-4xl md:text-5xl"
            } tracking-tight text-[var(--ink)] mb-4`}
            data-testid="rescue-title"
          >
            {t("rescue.title")}
          </h1>
          <p className="text-[15px] text-[var(--ink-soft)] italic max-w-lg mx-auto">
            {t("rescue.subtitle")}
          </p>
        </div>

        {/* Lookup form */}
        <form onSubmit={handleLookup} className="space-y-5 mb-10" data-testid="rescue-lookup-form">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.28em] text-[var(--ink-soft)] mb-3">
              {t("rescue.identifier_label")}
            </label>
            <input
              data-testid="rescue-identifier-input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={t("rescue.identifier_placeholder")}
              className="focus-line w-full text-[17px] py-3 text-[var(--ink)] placeholder:text-[var(--ink-mute)] placeholder:italic"
              required
            />
            <p className="mt-2 text-[12px] text-[var(--ink-mute)] italic">
              {t("rescue.identifier_hint")}
            </p>
          </div>
          <button
            type="submit"
            disabled={lookupState === "loading"}
            data-testid="rescue-lookup-btn"
            className="btn-wax w-full py-4 text-[12px] uppercase tracking-[0.24em] flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {lookupState === "loading" ? (
              <>
                <Loader2 className="animate-spin" size={14} /> {t("rescue.looking_up")}
              </>
            ) : (
              t("rescue.lookup_btn")
            )}
          </button>
        </form>

        {/* Error state */}
        {lookupState === "error" && (
          <div
            data-testid="rescue-error"
            className="flex items-start gap-3 p-5 border border-[var(--wax-deep)]/40 bg-[var(--wax-deep)]/5 text-[var(--wax-deep)] mb-6"
          >
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-[14px] mb-1">{t("rescue.not_found_title")}</p>
              <p className="text-[13px] italic">{error}</p>
            </div>
          </div>
        )}

        {/* Email-mode soft acknowledgement */}
        {emailModeMessage && (
          <div
            data-testid="rescue-email-mode"
            className="p-6 border border-[var(--rule)] bg-[var(--bg-paper-deep)]/40 mb-6"
          >
            {emailModeMessage.exists ? (
              <>
                <div className="flex items-center gap-3 text-[var(--wax)] mb-3">
                  <ShieldCheck size={18} />
                  <p className="font-display text-xl">{t("rescue.email_found_title")}</p>
                </div>
                <p className="text-[14px] text-[var(--ink-soft)] leading-[1.85] mb-3">
                  {emailModeMessage.is_delivered ? t("rescue.email_found_delivered") : t("rescue.email_found_pending")}
                </p>
                <p className="text-[13px] italic text-[var(--ink-mute)]">{t("rescue.email_use_stripe")}</p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 text-[var(--ink-soft)] mb-3">
                  <Mail size={18} />
                  <p className="font-display text-xl">{t("rescue.email_not_found")}</p>
                </div>
                <p className="text-[13px] italic text-[var(--ink-mute)]">{t("rescue.email_not_found_hint")}</p>
              </>
            )}
          </div>
        )}

        {/* Full capsule status (Stripe-ID mode) */}
        {capsule && (
          <div data-testid="rescue-capsule-card" className="border border-[var(--rule)] bg-[var(--bg-paper-deep)]/50 p-7 md:p-9 mb-6">
            <div className="flex items-center gap-3 text-[var(--wax)] mb-5">
              <ShieldCheck size={20} />
              <p className="text-[11px] uppercase tracking-[0.32em]">
                {capsule.is_delivered
                  ? t("rescue.status_delivered")
                  : t("rescue.status_tunnel_active", { days: capsule.days_remaining ?? "—" })}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-8 mb-6">
              <Stat label={t("rescue.field_prescription")} value={labelFor(capsule.prescription_tag, capsule.language_code)} />
              <Stat label={t("rescue.field_duration")} value={`${capsule.duration_days} ${isZH ? "天" : "days"}`} />
              <Stat label={t("rescue.field_delivery_email")} value={capsule.email_masked} mono />
              <Stat
                label={t("rescue.field_delivery_date")}
                value={capsule.delivery_date ? new Date(capsule.delivery_date).toLocaleDateString(isZH ? "zh-CN" : "en-US", { year: "numeric", month: "long", day: "numeric" }) : "—"}
              />
            </div>

            {/* Email update action */}
            {capsule.can_update_email && !showEditForm && updateState !== "ok" && (
              <button
                onClick={() => setShowEditForm(true)}
                data-testid="rescue-edit-email-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-[var(--ink)] text-[12px] uppercase tracking-[0.22em] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg-paper)] transition-colors"
              >
                <Mail size={13} /> {t("rescue.update_email_btn")}
              </button>
            )}

            {!capsule.can_update_email && updateState !== "ok" && (
              <p className="text-[12px] italic text-[var(--ink-mute)]">
                {capsule.is_delivered
                  ? t("rescue.update_blocked_delivered")
                  : capsule.payment_status !== "paid"
                  ? t("rescue.update_blocked_unpaid")
                  : t("rescue.update_blocked_already_used")}
              </p>
            )}

            {showEditForm && (
              <form onSubmit={handleUpdate} className="mt-6 pt-6 border-t border-dashed border-[var(--rule)] space-y-4" data-testid="rescue-edit-form">
                <p className="text-[12px] uppercase tracking-[0.28em] text-[var(--ink-soft)]">
                  {t("rescue.edit_form_title")}
                </p>
                <p className="text-[13px] italic text-[var(--ink-mute)]">{t("rescue.edit_form_warning")}</p>
                <input
                  type="email"
                  data-testid="rescue-new-email-input"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder={t("rescue.new_email_placeholder")}
                  className="focus-line w-full text-[16px] py-3 text-[var(--ink)] placeholder:text-[var(--ink-mute)] placeholder:italic"
                  required
                />
                {updateError && (
                  <p data-testid="rescue-update-error" className="text-[13px] italic text-[var(--wax-deep)]">
                    {updateError}
                  </p>
                )}
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={updateState === "saving"}
                    data-testid="rescue-confirm-update-btn"
                    className="btn-wax px-6 py-3 text-[12px] uppercase tracking-[0.22em] flex items-center gap-2 disabled:opacity-50"
                  >
                    {updateState === "saving" ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                    {t("rescue.confirm_update")}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowEditForm(false); setUpdateError(""); }}
                    className="px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--ink)]"
                  >
                    {t("rescue.cancel")}
                  </button>
                </div>
              </form>
            )}

            {updateState === "ok" && (
              <div data-testid="rescue-update-success" className="mt-6 pt-6 border-t border-dashed border-[var(--rule)] flex items-start gap-3 text-[var(--wax)]">
                <Check size={18} className="mt-0.5" />
                <div>
                  <p className="font-semibold text-[14px] mb-1">{t("rescue.update_success_title")}</p>
                  <p className="text-[13px] italic text-[var(--ink-soft)]">{t("rescue.update_success_body")}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Consent record — transparency block (only when capsule has stored consent) */}
        {capsule && capsule.consent && (
          <ConsentRecord
            data-testid="rescue-consent-record"
            consent={capsule.consent}
            identifier={identifier.trim()}
            t={t}
            isZH={isZH}
          />
        )}

        {/* Privacy reassurance footer */}
        <p className="text-center text-[12px] italic text-[var(--ink-mute)] mt-10">
          {t("rescue.privacy_footer")}
        </p>
      </div>
    </div>
  );
}

const Stat = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--ink-mute)] mb-1.5">{label}</div>
    <div className={`text-[var(--ink)] text-[15px] ${mono ? "font-mono" : ""}`}>{value}</div>
  </div>
);

const formatTs = (iso, isZH) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(isZH ? "zh-CN" : "en-US", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
};

const LANG_LABEL = { en: "English", "zh-HK": "繁中 · 港", "zh-TW": "繁中 · 台", "zh-CN": "简中" };

const ConsentRecord = ({ consent, identifier, t, isZH }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadErr, setDownloadErr] = useState("");

  const handleDownload = async () => {
    if (!identifier) return;
    setDownloading(true);
    setDownloadErr("");
    try {
      const r = await api.post(
        "/rescue/audit-pdf",
        { identifier },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "miprose-consent-record.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const data = e.response?.data;
      if (data instanceof Blob) {
        try {
          const txt = await data.text();
          const parsed = JSON.parse(txt);
          setDownloadErr(parsed.detail || txt);
        } catch {
          setDownloadErr(t("rescue.download_pdf_error"));
        }
      } else {
        setDownloadErr(e.response?.data?.detail || String(e));
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      data-testid="rescue-consent-record"
      className="border border-[var(--rule)] bg-[var(--bg-paper-deep)]/30 p-7 md:p-9 mb-6"
    >
      <div className="flex items-center gap-3 text-[var(--wax)] mb-2">
        <FileCheck2 size={18} />
        <p className="text-[11px] uppercase tracking-[0.32em]">{t("rescue.consent_section_title")}</p>
      </div>
      <p className="text-[13px] italic text-[var(--ink-soft)] mb-6 leading-[1.75]">
        {t("rescue.consent_intro")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-8">
        <Stat
          label={t("rescue.consent_field_accepted_at")}
          value={formatTs(consent.client_accepted_at, isZH)}
        />
        <Stat
          label={t("rescue.consent_field_language")}
          value={LANG_LABEL[consent.language_code] || consent.language_code || "—"}
        />
        <Stat
          label={t("rescue.consent_field_policy_version")}
          value={consent.policy_version || "—"}
          mono
        />
        <Stat
          label={t("rescue.consent_field_device")}
          value={consent.device_family || "—"}
        />
        <div className="sm:col-span-2">
          <Stat
            label={t("rescue.consent_field_ip")}
            value={consent.ip_fingerprint || "—"}
            mono
          />
        </div>
      </div>
      <div className="mt-7 pt-5 border-t border-dashed border-[var(--rule)] flex flex-wrap items-center gap-x-6 gap-y-3">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          data-testid="rescue-download-pdf-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--ink)] text-[var(--bg-paper)] text-[12px] uppercase tracking-[0.22em] hover:bg-[var(--wax)] transition-colors disabled:opacity-50"
        >
          {downloading ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />}
          {downloading ? t("rescue.download_pdf_loading") : t("rescue.download_pdf_btn")}
        </button>
        <a
          href="/terms"
          data-testid="rescue-view-policy-link"
          className="text-[12px] uppercase tracking-[0.22em] text-[var(--wax)] hover:text-[var(--ink)] border-b border-[var(--wax)]/60 hover:border-[var(--ink)] pb-0.5"
        >
          {t("rescue.consent_view_policy")} →
        </a>
      </div>
      <p className="mt-3 text-[12px] italic text-[var(--ink-mute)]">
        {t("rescue.download_pdf_hint")}
      </p>
      {downloadErr && (
        <p data-testid="rescue-download-pdf-error" className="mt-2 text-[12px] italic text-[var(--wax-deep)]">
          {downloadErr}
        </p>
      )}
    </div>
  );
};
